import {
    ArrowPathIcon,
    ChartBarIcon,
    ChatBubbleOvalLeftIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    PencilSquareIcon,
    SparklesIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import type { PatientAnalysis } from '../../../services/analysis';
import type { AnalysisVariant } from './healthConfig';

// The data the generation job reads - shown on the empty state and mirrored by
// the sources bar on the success view (see AnalysisResultView).
export const ANALYSIS_SOURCES = [
    { icon: ChartBarIcon, label: 'Timeline & activity' },
    { icon: PencilSquareIcon, label: 'Patient notes' },
    { icon: Squares2X2Icon, label: 'Patient data and statuses' },
    { icon: ChatBubbleOvalLeftIcon, label: 'Chat (external + internal)' },
    { icon: ChatBubbleOvalLeftIcon, label: 'Dental Monitoring chat' },
    { icon: DocumentTextIcon, label: 'Treatment & scan data' }
] as const;

interface AnalysisEmptyProps {
    onRun: () => void;
    isStarting: boolean;
    /** 'slim': source chips stack in one column for narrow containers. */
    variant?: AnalysisVariant;
}

function AnalysisEmpty({ onRun, isStarting, variant = 'full' }: AnalysisEmptyProps) {
    const slim = variant === 'slim';
    return (
        <div className={`flex-1 flex items-center justify-center ${slim ? 'p-5' : 'p-8'}`}>
            <div className="max-w-[460px] w-full text-center flex flex-col items-center">
                <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white mb-4">
                    <SparklesIcon className="w-7 h-7" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">No analysis yet</h2>
                <p className="text-[13px] text-gray-500 leading-relaxed mt-1.5">
                    Run a situational analysis to get a dated snapshot of where this patient stands
                    - a health score, TL;DR, recap and recommended action items - generated from
                    everything we know about them.
                </p>
                <button
                    onClick={onRun}
                    disabled={isStarting}
                    className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-black shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <SparklesIcon className="w-4 h-4" />
                    {isStarting ? 'Starting…' : 'Run analysis'}
                </button>
                <div className="mt-8 w-full text-left">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-2.5">
                        What it reads
                    </div>
                    <div className={`grid gap-2 ${slim ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {ANALYSIS_SOURCES.map((s) => (
                            <div
                                key={s.label}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50"
                            >
                                <s.icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-[12px] font-medium text-gray-600">
                                    {s.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AnalysisPending() {
    return (
        <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-[440px] w-full">
                <div className="rounded-2xl border border-gray-200 bg-white p-6">
                    <div className="flex items-center gap-3">
                        <div className="relative w-10 h-10 flex-shrink-0">
                            <div className="absolute inset-0 rounded-full border-[3px] border-gray-100" />
                            <div className="absolute inset-0 rounded-full border-[3px] border-gray-900 border-t-transparent animate-spin" />
                            <div className="absolute inset-0 flex items-center justify-center text-gray-700">
                                <SparklesIcon className="w-4 h-4" />
                            </div>
                        </div>
                        <div>
                            <div className="text-sm font-bold text-gray-900">Analyzing…</div>
                            <div className="text-[11px] text-gray-400">
                                Running in the background · usually takes about a minute
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-50 text-amber-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />{' '}
                            Pending
                        </span>
                        <span className="text-[11px] text-gray-400">
                            You can leave this tab - it&apos;ll finish on its own.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface AnalysisFailedProps {
    analysis: PatientAnalysis;
    onRetry: () => void;
    isRetrying: boolean;
}

function AnalysisFailed({ analysis, onRetry, isRetrying }: AnalysisFailedProps) {
    const failedAt = analysis.completedAt ?? analysis.updatedAt;
    return (
        <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-[440px] w-full">
                <div className="rounded-2xl border border-red-200 bg-red-50/50 p-6 text-center flex flex-col items-center">
                    <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center text-red-600 mb-3">
                        <ExclamationTriangleIcon className="w-6 h-6" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900">Analysis failed</h2>
                    <p className="text-[13px] text-gray-600 leading-relaxed mt-1.5">
                        The situational analysis couldn&apos;t complete. No result was saved - you
                        can safely re-run it.
                    </p>
                    {analysis.error && (
                        <div className="mt-4 w-full text-left rounded-lg border border-red-200 bg-white px-3 py-2.5">
                            <div className="text-[10px] uppercase tracking-wider font-semibold text-red-400 mb-0.5">
                                Error
                            </div>
                            <code className="text-[12px] font-mono text-red-700 leading-snug break-words">
                                {analysis.error}
                            </code>
                        </div>
                    )}
                    {failedAt && (
                        <div className="text-[11px] text-gray-400 mt-2.5">
                            Failed {format(new Date(failedAt), 'MMM d, yyyy · h:mm a')}
                        </div>
                    )}
                    <button
                        onClick={onRetry}
                        disabled={isRetrying}
                        className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-black shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ArrowPathIcon className="w-4 h-4" />
                        {isRetrying ? 'Retrying…' : 'Retry analysis'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export { AnalysisEmpty, AnalysisPending, AnalysisFailed };
